/**
 * Size guide (Figma node 1:1280).
 *
 * Progressive enhancement for the size guide rendered by snippets/size-guide.liquid. The server renders every value in
 * its source unit; this element wires up the size chips (which drive the diagram callouts and keep the matching table
 * column in view) and the centimetres/inches toggle (which converts every numeric cell and callout). It also sizes
 * the chip grid to its widest label (see fitChips) so long labels never overflow their chip on narrow screens.
 *
 * Data attributes:
 *   .size-guide__options                  chip grid; receives --size-guide-chip-min (the widest label's width)
 *   [data-size-select][data-size-index]   size chip
 *   [data-unit="cm|in"]                   unit chip
 *   table [data-size-index]               header/body cell in a size column; the selected column gets .is-active-col
 *   [data-measure-value][data-source-unit] table cell holding a numeric value (or range, e.g. "86-90")
 *   [data-callout][data-values][data-source-unit] diagram pill; data-values is "|"-separated, one entry per size
 */
(function () {
  if (customElements.get('size-guide')) {
    return;
  }

  const CM_PER_INCH = 2.54;

  function convertNumber(value, from, to) {
    if (from === to) {
      return value;
    }

    if (to === 'in') {
      return Math.round((value / CM_PER_INCH) * 2) / 2;
    }

    return Math.round(value * CM_PER_INCH);
  }

  function formatNumber(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }

  /**
   * Convert every number inside a raw value ("86", "86-90", "86 / 90") and append the unit suffix. Values without a
   * number are returned untouched so "-" or "n/a" cells survive the toggle.
   */
  function formatValue(raw, from, to) {
    const text = (raw || '').trim();

    if (!/\d/.test(text)) {
      return text;
    }

    const converted = text.replace(/\d+(\.\d+)?/g, function (match) {
      return formatNumber(convertNumber(parseFloat(match), from, to));
    });

    return to === 'in' ? converted + '"' : converted + ' cm';
  }

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      this.unit = 'cm';
      this.activeIndex = 0;

      this.optionGroups = Array.from(this.querySelectorAll('.size-guide__options'));
      this.sizeButtons = Array.from(this.querySelectorAll('[data-size-select]'));
      this.unitButtons = Array.from(this.querySelectorAll('button[data-unit]'));
      this.cells = Array.from(this.querySelectorAll('[data-measure-value]'));
      this.columnHeads = Array.from(this.querySelectorAll('thead [data-size-index]'));
      this.columnCells = Array.from(this.querySelectorAll('table [data-size-index]'));
      this.callouts = Array.from(this.querySelectorAll('[data-callout]'));

      const presetSize = this.sizeButtons.findIndex(function (button) {
        return button.getAttribute('aria-pressed') === 'true';
      });
      if (presetSize > -1) {
        this.activeIndex = presetSize;
      }

      const presetUnit = this.unitButtons.find(function (button) {
        return button.getAttribute('aria-pressed') === 'true';
      });
      if (presetUnit) {
        this.unit = presetUnit.dataset.unit === 'in' ? 'in' : 'cm';
      }

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      // The guide lives in a modal that is display: none until opened (and is re-parented to <body> on open), so the
      // chips have no size to measure yet. The observer fires once the modal is shown and again on resize.
      this.fitChips = this.fitChips.bind(this);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.fitChips);
        this.resizeObserver.observe(this);
      } else {
        this.fitChips();
      }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.fitChips);
      }

      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }

    /**
     * Size each chip grid to its widest label. The chips are equal-width grid columns
     * (`repeat(auto-fit, minmax(--size-guide-chip-min, 1fr))`), so the column minimum has to be at least as wide as
     * the longest label or that label spills out of its chip on narrow screens. The label is measured with a Range,
     * which gives the text's natural width regardless of how squeezed the chip's box currently is.
     */
    fitChips() {
      if (!this.isConnected || this.clientWidth === 0) {
        return;
      }

      this.optionGroups.forEach(function (group) {
        let widest = 0;

        group.querySelectorAll('.size-guide__chip').forEach(function (chip) {
          const style = getComputedStyle(chip);
          const range = document.createRange();
          range.selectNodeContents(chip);

          const chrome =
            parseFloat(style.paddingLeft) +
            parseFloat(style.paddingRight) +
            parseFloat(style.borderLeftWidth) +
            parseFloat(style.borderRightWidth);

          widest = Math.max(widest, range.getBoundingClientRect().width + chrome);
        });

        if (widest > 0) {
          const value = Math.ceil(widest) + 'px';

          if (group.style.getPropertyValue('--size-guide-chip-min') !== value) {
            group.style.setProperty('--size-guide-chip-min', value);
          }
        }
      });
    }

    onClick(event) {
      const sizeButton = event.target.closest('[data-size-select]');
      if (sizeButton && this.contains(sizeButton)) {
        this.activeIndex = Number(sizeButton.dataset.sizeIndex) || 0;
        this.render();
        this.revealColumn();
        return;
      }

      const unitButton = event.target.closest('button[data-unit]');
      if (unitButton && this.contains(unitButton)) {
        this.unit = unitButton.dataset.unit === 'in' ? 'in' : 'cm';
        this.render();
      }
    }

    /**
     * Scroll the table horizontally (never the page) so the selected size's column is visible. The first column is
     * sticky, so it is subtracted from the visible area.
     */
    revealColumn() {
      const activeIndex = this.activeIndex;
      const head = this.columnHeads.find(function (cell) {
        return Number(cell.dataset.sizeIndex) === activeIndex;
      });
      const wrap = head ? head.closest('.size-guide__table-wrap') : null;

      if (!head || !wrap) {
        return;
      }

      const stickyHead = wrap.querySelector('thead .size-guide__rowhead');
      const stickyWidth = stickyHead ? stickyHead.offsetWidth : 0;
      const columnStart = head.offsetLeft;
      const columnEnd = columnStart + head.offsetWidth;
      const visibleStart = wrap.scrollLeft + stickyWidth;
      const visibleEnd = wrap.scrollLeft + wrap.clientWidth;
      let target = null;

      if (columnStart < visibleStart) {
        target = columnStart - stickyWidth;
      } else if (columnEnd > visibleEnd) {
        target = columnEnd - wrap.clientWidth;
      }

      if (target !== null) {
        wrap.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
    }

    render() {
      const activeIndex = this.activeIndex;
      const unit = this.unit;

      this.sizeButtons.forEach(function (button) {
        const active = Number(button.dataset.sizeIndex) === activeIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.unitButtons.forEach(function (button) {
        const active = (button.dataset.unit === 'in' ? 'in' : 'cm') === unit;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.cells.forEach(function (cell) {
        cell.textContent = formatValue(cell.dataset.measureValue, cell.dataset.sourceUnit || 'cm', unit);
      });

      // Highlight the selected size's column (PAPCM-146: the grey shading alone was too subtle).
      this.columnCells.forEach(function (cell) {
        cell.classList.toggle('is-active-col', Number(cell.dataset.sizeIndex) === activeIndex);
      });

      this.callouts.forEach(function (callout) {
        const valueEl = callout.querySelector('[data-callout-value]');
        if (!valueEl) {
          return;
        }

        const values = (callout.dataset.values || '').split('|');
        valueEl.textContent = formatValue(values[activeIndex], callout.dataset.sourceUnit || 'cm', unit) || '\u2014';
      });
    }
  }

  customElements.define('size-guide', SizeGuide);
})();
